import {
  Button,
  Column,
  DataRow,
  DateCellComponent,
  FlexContainer,
  FullscreenSheet,
  InputText,
  ScannerInput,
  SoundNotificationContext,
  Span,
  TableSimple,
  TriangleAlertIcon,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorMessageAsync } from '@shared/api';
import { getWeight, sendParcelWeight } from '../api';

type WeightSource = 'scales' | 'manual';

type ChangedWeightRow = DataRow<{
  id: string;
  trackNumber: string;
  weight: number;
  source: WeightSource;
  changedAt: Date;
}>;

const isValidWeight = (weight: number) => Number.isFinite(weight) && weight > 0 && weight < 30000;

export default function ChangeWeightModal(props: {
  isOpenChangeWeight: boolean;
  onChangeOpen: (isOpen: boolean) => void;
  changeWeightSuccessList: (weight: number, trackNumber: string) => void;
}) {
  const { onChangeOpen, isOpenChangeWeight, changeWeightSuccessList } = props;
  const { createNotification } = useNotificationsV2();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { t, i18n } = useTranslation();

  // null — ещё не проверяли подключение к весам
  const [scalesConnected, setScalesConnected] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Трек, для которого весы недоступны и вес ждём руками
  const [manualTrackNumber, setManualTrackNumber] = useState<string | null>(null);
  const [manualWeight, setManualWeight] = useState('');
  const [changedRows, setChangedRows] = useState<ChangedWeightRow[]>([]);
  const manualInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpenChangeWeight) return;
    const controller = new AbortController();
    getWeight(controller.signal).then((res) => {
      if (!controller.signal.aborted) setScalesConnected(Boolean(res));
    });
    return () => controller.abort();
  }, [isOpenChangeWeight]);

  useEffect(() => {
    if (manualTrackNumber) manualInputRef.current?.focus();
  }, [manualTrackNumber]);

  const showError = (title: string, desc?: string) => {
    createNotification({ type: 'error', message: { title, desc } });
    onSoundError();
  };

  const close = () => {
    if (isLoading) return;
    onChangeOpen(false);
    setScalesConnected(null);
    setManualTrackNumber(null);
    setManualWeight('');
    setChangedRows([]);
  };

  const submitWeight = async (trackNumber: string, weight: number, source: WeightSource) => {
    if (!isValidWeight(weight)) {
      showError(t('weightError'));
      return false;
    }

    setIsLoading(true);
    try {
      await sendParcelWeight({ trackNumber, weight: { unit: 0, value: weight } });
      setChangedRows((prev) => [
        { id: `${trackNumber}-${Date.now()}`, trackNumber, weight, source, changedAt: new Date() },
        ...prev,
      ]);
      changeWeightSuccessList(weight, trackNumber);
      onSoundSuccess();
      return true;
    } catch (e) {
      showError(t('common.systemError.title'), await getErrorMessageAsync(e, t('common.systemError.desc')));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const onScanSubmit = async (trackNumber: string) => {
    if (isLoading || manualTrackNumber) return;

    setIsLoading(true);
    const weightResponse = await getWeight();
    setIsLoading(false);

    // Весы не ответили — просим ввести вес руками для этого трека
    if (!weightResponse) {
      setScalesConnected(false);
      setManualTrackNumber(trackNumber);
      return;
    }

    setScalesConnected(true);
    if (!weightResponse.weight) {
      showError(t('consolidatorTracking.changeWeight.emptyScales'));
      return;
    }

    await submitWeight(trackNumber, weightResponse.weight, 'scales');
  };

  const onManualSubmit = async () => {
    if (!manualTrackNumber || isLoading || !manualWeight) return;
    const ok = await submitWeight(manualTrackNumber, Number.parseInt(manualWeight, 10), 'manual');
    if (ok) {
      setManualTrackNumber(null);
      setManualWeight('');
    }
  };

  const cancelManual = () => {
    setManualTrackNumber(null);
    setManualWeight('');
  };

  const setManualInputRef = useCallback((element: HTMLInputElement | null) => {
    manualInputRef.current = element;
  }, []);

  const columns: Column<ChangedWeightRow>[] = useMemo(
    () => [
      {
        header: t('tableHeader.trackNumber'),
        accessor: 'trackNumber',
        renderCell: (row) => <span className="ct6-code">{row.trackNumber}</span>,
        flex: 2,
        textAlign: 'center',
        minWidth: '160px',
      },
      { header: t('tableHeader.weight'), accessor: 'weight', textAlign: 'center', flex: 1, minWidth: '100px' },
      {
        header: t('consolidatorTracking.changeWeight.source'),
        accessor: 'source',
        renderCell: (row) => t(`consolidatorTracking.changeWeight.sources.${row.source}`),
        textAlign: 'center',
        flex: 1,
        minWidth: '120px',
      },
      {
        header: t('tableHeader.scanAt'),
        accessor: 'changedAt',
        renderCell: (row) => <DateCellComponent justify="center" date={row.changedAt} zone="local" />,
        textAlign: 'center',
        flex: 1,
        minWidth: '200px',
      },
    ],
    [t],
  );

  return (
    <FullscreenSheet height={'100dvh'} open={isOpenChangeWeight} onClose={close}>
      <FlexContainer direction="column" gap="16px">
        <FlexContainer justify="space-between" align="center" gap="16px">
          <FlexContainer direction="column" gap="4px">
            <Span fs={24} fw={600}>
              {t('changeWeight')}
            </Span>
            {scalesConnected !== null && (
              <Span fs={14} color={scalesConnected ? undefined : 'red'}>
                {scalesConnected
                  ? t('consolidatorTracking.changeWeight.scalesConnected')
                  : t('consolidatorTracking.changeWeight.scalesDisconnected')}
              </Span>
            )}
          </FlexContainer>
          <Button width="100px" disabled={isLoading} onClick={close}>
            {t('tableHeader.rollUp')}
          </Button>
        </FlexContainer>

        <ScannerInput
          label={t('consolidatorTracking.scan.trackNumberLabel')}
          buttonLabel={t('consolidatorTracking.scan.scanButton')}
          isLoading={isLoading}
          disabled={isLoading || Boolean(manualTrackNumber)}
          maxValueLength={64}
          lang={i18n.language}
          isFocusFilter={Boolean(manualTrackNumber)}
          onSubmit={onScanSubmit}
        />

        {manualTrackNumber && (
          <div className="ct6-manual">
            <div className="ct6-warning">
              <TriangleAlertIcon color="red" />
              <span>{t('consolidatorTracking.changeWeight.manualWarning', { trackNumber: manualTrackNumber })}</span>
            </div>
            <InputText
              id="change-weight-manual"
              ref={setManualInputRef}
              type="number"
              textAlign="left"
              autoComplete="off"
              label={t('consolidatorTracking.manual.weightLabel')}
              disabled={isLoading}
              value={manualWeight}
              onChange={setManualWeight}
              onKeyDown={(event) => {
                if (event.key === 'Escape') cancelManual();
                if (event.key !== 'Enter') return;
                event.preventDefault();
                event.stopPropagation();
                void onManualSubmit();
              }}
            />
            <FlexContainer justify="space-between" align="center" gap="8px">
              <div className="ct6-subhint">{t('consolidatorTracking.changeWeight.manualHint')}</div>
              <Button type="secondary" width="120px" disabled={isLoading} onClick={cancelManual}>
                {t('cancel')}
              </Button>
            </FlexContainer>
          </div>
        )}

        <div className="ct6-card">
          <div className="ct6-cardHead">
            <div className="ct6-title">{t('consolidatorTracking.changeWeight.tableTitle')}</div>
            <div className="ct6-counter">{changedRows.length}</div>
          </div>
          <TableSimple<ChangedWeightRow>
            columns={columns}
            data={changedRows}
            enableColumnResizing={false}
            withOutPagination
            isLoading={false}
            emptyPlaceholder={t('consolidatorTracking.changeWeight.empty')}
            mobileWidth={680}
            maxHeightTable="calc(100dvh - 380px)"
          />
        </div>
      </FlexContainer>
    </FullscreenSheet>
  );
}
